import React from 'react'

import ServiceLayout from './ServiceLayout'
import {
  CheckCircle, Info, Table as TableIcon, FileText, Clock, Tag, Shield,
  Lock, Cloud, HardDrive, ShieldCheck, Activity, Layers, Sparkles,
  Microscope, Dna, ArrowRight, Database, Atom, Flower2, Pill,
  Users, UserCheck, ShieldAlert
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ServiceCTA from './ServiceCTA'

const isDeliver = (tObj) => {
  const lowerHeading = (tObj.heading || '').toLowerCase();
  return lowerHeading.includes('what we deliver') || lowerHeading.includes('成果物') || (tObj.id && tObj.id.includes('what-we-deliver'));
};

/** Hide duplicate H4 where the module title already states the assay name (JP + EN labels). */
const shouldShowWorkflowTableHeading = (heading) =>
  heading && heading !== 'メタトランスクリプトミクス' && heading !== 'Metatranscriptomics';

const ThreeColumnServiceRenderer = ({ tables, t, serviceKey }) => {
  if (!tables || tables.length === 0) return null;

  const provideTable = tables.find(tObj => !isDeliver(tObj));
  const deliverTable = tables.find(tObj => isDeliver(tObj));

  if (!provideTable) return null;

  let sections = [];
  (provideTable.workflow || []).forEach(item => {
    if (/^\d+\.\s/.test(item)) {
       sections.push({ title: item.replace(/^\d+\.\s/, ''), items: [] });
    } else {
       let cleanItem = item.replace(/^[・.•]\s*/, '');
       if (sections.length > 0) sections[sections.length - 1].items.push(cleanItem);
       else sections.push({ title: '', items: [cleanItem] });
    }
  });

  const iconMap = {
    biomarkerDiscovery: [Layers, Activity, ShieldCheck],
    cohortReanalysis: [Database, ShieldCheck, Sparkles],
    agricultureFood: [Microscope, Dna, ShieldCheck, Sparkles]
  };

  const icons = iconMap[serviceKey] || [Layers, Activity, Sparkles];

  // Keyword-based icon mapping for section titles
  const keywordIconMap = {
    '形質': Microscope,
    '生物学的': Microscope,
    'biological': Microscope,
    'trait': Microscope,
    'バイオプロセス': Dna,
    'bioprocess': Dna,
    'ものづくり': Dna,
    'biofabrication': Dna,
    '食品安全': ShieldCheck,
    'safety': ShieldCheck,
    'トレーサビリティ': ShieldCheck,
    'traceability': ShieldCheck,
    'パーソナライズド': Sparkles,
    'personalized': Sparkles,
    '栄養': Flower2,
    'nutrition': Flower2,
    '最適化': Activity,
    'optimization': Activity,
    '育種': Atom,
    'breeding': Atom,
    '発酵': Pill,
    'fermentation': Pill,
    '菌株': Atom,
    'strain': Atom,
    'タンパク質': Atom,
    'protein': Atom,
    '酵素': Atom,
    'enzyme': Atom,
    '汚染': ShieldAlert,
    'contamination': ShieldAlert,
    '病原体': ShieldCheck,
    'pathogen': ShieldCheck,
    '宿主': Atom,
    'host': Atom,
    '遺伝子': Dna,
    'genetics': Dna,
    'レスポンダー': UserCheck,
    'responder': UserCheck,
    '層別化': Users,
    'stratification': Users,
  };

  const getKeywordIcon = (title) => {
    if (!title) return Sparkles;
    for (const [keyword, Icon] of Object.entries(keywordIconMap)) {
      if (title.toLowerCase().includes(keyword.toLowerCase())) {
        return Icon;
      }
    }
    return null;
  };

  // Agriculture & Food: minimal left-accented list layout (no card bg)
  if (serviceKey === 'agricultureFood') {
    return (
      <div className="space-y-12">
        {sections.map((section, idx) => {
          const keywordIcon = getKeywordIcon(section.title);
          const Icon = keywordIcon || icons[idx] || Sparkles;

          const titleMatch = section.title.match(/^(.+?)（(.+?)）$/);
          const jpTitle = titleMatch ? titleMatch[1] : section.title;
          const enTitle = titleMatch ? titleMatch[2] : null;

          return (
            <div key={idx} className="flex flex-col sm:flex-row gap-5 animate-in fade-in slide-in-from-bottom-6 duration-1000" style={{ animationDelay: `${idx * 150}ms` }}>
              {/* Icon */}
              <div className="sm:w-20 flex-shrink-0 flex sm:flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="w-0.5 flex-1 sm:h-8 bg-primary/10 rounded-full hidden sm:block sm:mt-2" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-1">
                <div className="mb-4">
                  <h3 className="text-base font-display font-bold text-text-main">{jpTitle}</h3>
                  {enTitle && <p className="text-xs text-text-dim font-medium mt-0.5">{enTitle}</p>}
                </div>

                <ul className="space-y-3 border-l-2 border-primary/10 ml-1">
                  {section.items.map((item, iIdx) => (
                    <li key={iIdx} className="flex items-start gap-3 group pl-4">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-2 flex-shrink-0 group-hover:bg-primary transition-colors" />
                      <span className="text-base text-text-muted leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Grid for Workflow Sections */}
      <div className={`grid grid-cols-1 ${serviceKey === 'agricultureFood' ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-6`}>
        {sections.map((section, idx) => {
          const keywordIcon = getKeywordIcon(section.title);
          const Icon = keywordIcon || icons[idx] || Sparkles;

          // Parse title: split "JP（EN）" into separate lines
          const titleMatch = section.title.match(/^(.+?)（(.+?)）$/);
          const jpTitle = titleMatch ? titleMatch[1] : section.title;
          const enTitle = titleMatch ? titleMatch[2] : null;

          return (
            <div key={idx} className="bio-card-tinted p-6 flex flex-col h-full min-h-fit animate-in fade-in slide-in-from-bottom-6 duration-1000" style={{ animationDelay: `${idx * 150}ms` }}>
              <div className="w-12 h-12 rounded-2xl bg-white shadow-soft flex items-center justify-center text-primary mb-6 shrink-0">
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-display font-bold text-text-main mb-1">{jpTitle}</h3>
              {enTitle && <p className="text-xs text-text-dim font-medium mb-4">{enTitle}</p>}
              {!enTitle && <div className="mb-4" />}
              <ul className="space-y-4 flex-1">
                {section.items.map((item, iIdx) => (
                  <li key={iIdx} className="flex items-start gap-2.5 group">
                    <CheckCircle className="w-3.5 h-3.5 text-primary mt-0.5 opacity-60 flex-shrink-0" />
                    <span className="text-sm text-text-muted leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>

      {/* Deliverables Block */}
      {deliverTable && serviceKey !== 'agricultureFood' && (
        <div id={deliverTable.id} className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm scroll-mt-32 mt-8 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-500">
           <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light flex items-center justify-between">
              <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2">
                <TableIcon className="w-4 h-4 text-primary" />
                {deliverTable.heading || 'What we deliver'}
              </h3>
           </div>
           <div className="px-4 sm:px-6 py-4">
              <ul className="space-y-3">
                {deliverTable.workflow.map((item, iIdx) => {
                  const isPara = iIdx === 0 && !/^・/.test(item) && !/^•/.test(item) && !item.includes(':') && !item.includes('：');
                  const isJaDeliverIntro = item.includes('以下の成果物を提供します：');
                  const isEnDeliverIntro = item.includes('consisting of:');

                  if (isPara || isJaDeliverIntro || isEnDeliverIntro) {
                    return (
                      <div key={iIdx} className="mb-4 text-sm font-bold text-text-muted leading-relaxed">
                        {item}
                      </div>
                    )
                  }

                  let cleanItem = item.replace(/^[・.•]\s*/, '').replace(/^\d+\.\s/, '');
                  
                  // Check for #...# bold without checkmark
                  const hashBoldMatch = cleanItem.match(/^#(.+)#$/);
                  if (hashBoldMatch) {
                    return (
                      <li key={iIdx} className="flex items-start gap-2.5 group">
                        <span className="text-sm font-bold text-text-muted leading-relaxed">{hashBoldMatch[1]}</span>
                      </li>
                    )
                  }
                  
                  return (
                    <li key={iIdx} className="flex items-start gap-2.5 group">
                      <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      <span className="text-sm text-text-muted leading-relaxed">{cleanItem}</span>
                    </li>
                  )
                })}
              </ul>
           </div>
        </div>
      )}
    </div>
  );
};

const AiMlTableRenderer = ({ tables, t }) => {
  if (!tables || tables.length === 0) return null;

  const provideTables = tables.filter(tObj => !isDeliver(tObj));
  const deliverTables = tables.filter(tObj => isDeliver(tObj));

  // Icon mapping for AI/ML section titles
  const sectionIconMap = {
    'integration': 'hub',
    'multi-dimensional': 'hub',
    'data integration': 'hub',
    'stratification': 'group_work',
    'methodologies': 'experiment',
    'clustering': 'scatter_plot',
    'biological': 'biotech',
    'validation': 'verified',
    'interpretability': 'psychology',
    'interpretation': 'insights',
    'feature': 'feature_search',
    'biomarker': 'science',
    'predictive': 'trending_up',
    'prediction': 'trending_up',
    'prognostic': 'monitoring',
    'survival': 'surveillance',
    'risk': 'warning',
    'target': 'target',
    'identification': 'fingerprint',
    'hit': 'casino',
    'discovery': 'search',
    'lead': 'timeline',
    'optimization': 'tune',
    'design': 'design_services',
    'multi-parameter': 'tune',
    'multiobjective': 'tune',
    'safety': 'security',
    'admet': 'medical_services',
    'toxicity': 'hazard',
    'developability': 'filter_alt',
    'exposure-response': 'show_chart',
    'pk/pd': 'monitor_heart',
    'pharmacokinetic': 'monitor_heart',
    'mechanistic': 'schema',
    'cohort': 'groups',
    're-analysis': 'manage_search',
    'reanalysis': 'manage_search',
    'advanced': 'auto_awesome',
    'biological modeling': 'model_training',
    'qc': 'fact_check',
    'preprocessing': 'cleaning_services',
    'cell-level': 'cell_merge',
    'annotation': 'label_important',
    'comparative': 'compare',
    'statistical': 'bar_chart',
    'differential': 'difference',
    'diversity': 'pie_chart',
    'functional': 'functions',
    'pathway': 'account_tree',
    'network': 'hub',
    'trajectory': 'show_chart',
    'outcome': 'flag',
    'subtype': 'account_tree',
    'characterization': 'fingerprint',
    'grounding': 'foundation',
    'molecular': 'science',
    'clinical': 'medical_information',
    'shap': 'insights',
    'attribution': 'attribution',
    'enrichment': 'grain',
    'mechanistic interpretation': 'psychology',
    'longitudinal': 'timeline',
    'progression': 'moving_beds',
    'heterogeneity': 'hub',
    'supervised': 'school',
    'semi-supervised': 'school',
    'latent factor': 'data_object',
    'autoencoder': 'neurology',
    'matrix factorization': 'grid_view',
    'consensus clustering': 'groups',
    'ensemble': 'account_tree',
    'outcome-linked': 'link',
    'responders': 'how_to_reg',
    'multi-omics': 'account_tree',
    'imaging': 'image_search',
    'radiomics': 'scan',
    'serial measurements': 'timeline',
    'progression patterns': 'show_chart',
    'treatment response': 'medication_liquid',
    'clinical & ehr': 'medical_information',
    'demographics': 'people',
    'transcriptomics': 'genetics',
    'proteomics': 'biotech',
    'metabolomics': 'science',
    'microbiome': 'grass',
    'mri/ct/pathology': 'scan',
    'patient subgroups': 'group_work',
    'stable subtypes': 'account_tree',
    'disease course': 'timeline',
    'endpoints': 'flag',
    'feature attribution': 'insights',
    'pathway/network': 'hub',
    'enrichment signals': 'grain',
    'breeding': 'agriculture',
    'trait improvement': 'grass',
    'genomics-assisted': 'genetics',
    'stress tolerance': 'shield',
    'multi-omics profiling': 'account_tree',
    'pathway identification': 'route',
    'soil microbiome': 'grass',
    'gut microbiota': 'biology',
    'biofertilizer': 'grass',
    'microbial consortium': 'hub',
    'fermentation': 'science',
    'strain': 'genetics',
    'metabolic engineering': 'biotech',
    'protein engineering': 'biotech',
    'enzyme': 'science',
    'genome-scale': 'grid_view',
    'dbtl': 'cycle',
    'food safety': 'verified',
    'contamination tracing': 'search',
    'host genetics': 'genetics',
    'personalized nutrition': 'nutrition',
    'responder identification': 'how_to_reg',
    'predictive modeling': 'model_training',
    'stratification strategy': 'group_work',
  };

  const getSectionIcon = (title) => {
    if (!title) return 'account_tree';
    const lowerTitle = title.toLowerCase();
    
    for (const [keyword, icon] of Object.entries(sectionIconMap)) {
      if (lowerTitle.includes(keyword)) {
        return icon;
      }
    }
    
    return 'account_tree'; // fallback
  };

  return (
    <>
      {provideTables.length > 0 && (
        <div className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm scroll-mt-32">
          <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light flex items-center justify-between">
            <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2">
              <TableIcon className="w-4 h-4 text-primary" />
              {(!provideTables[0].heading || provideTables[0].heading.toLowerCase().includes('what we provide'))
                ? t('technicalService.whatWeProvide', 'What we provide')
                : provideTables[0].heading}
            </h3>
          </div>
          <div className="w-full">
            {provideTables.map((table, ptIdx) => {
              let sections = [];
              const hasNumberedTitles = table.workflow && table.workflow.some(item => /^\d+\.\s/.test(item));
              const isGenericHeading = !table.heading || table.heading.toLowerCase().includes('what we provide');

              if (!hasNumberedTitles) {
                // Detect if workflow has section titles (items without colons) followed by bullet items (with colons)
                const hasSectionTitles = table.workflow && table.workflow.some(item => 
                  !item.includes(':') && !item.startsWith('・') && !item.startsWith('•') && !/^\d+\.\s/.test(item) && item.length < 60
                );

                if (hasSectionTitles) {
                  // Parse section titles and their items
                  let currentSection = null;
                  (table.workflow || []).forEach(item => {
                    const isSectionTitle = !item.includes(':') && !/^[・.•]/.test(item) && !/^\d+\.\s/.test(item) && item.length < 60;
                    if (isSectionTitle) {
                      currentSection = { title: item, items: [] };
                      sections.push(currentSection);
                    } else {
                      let cleanItem = item.replace(/^[・.•]\s*/, '');
                      if (currentSection) { currentSection.items.push(cleanItem); }
                      else { sections.push({ title: '', items: [cleanItem] }); }
                    }
                  });
                } else if (isGenericHeading) {
                  // Skip section title if heading is generic "What we provide"
                  sections.push({ title: '', items: table.workflow || [] });
                } else {
                  let cleanHeading = (table.heading || '').replace(/^What we provide:\s*/i, '');
                  sections.push({ title: cleanHeading, items: table.workflow || [] });
                }
              } else {
                let currentSection = null;
                (table.workflow || []).forEach(item => {
                  if (/^\d+\.\s/.test(item)) {
                    currentSection = { title: item.replace(/^\d+\.\s/, ''), items: [] };
                    sections.push(currentSection);
                  } else {
                    let cleanItem = item.replace(/^[・.•]\s*/, '');
                    if (currentSection) { currentSection.items.push(cleanItem); }
                    else { sections.push({ title: '', items: [cleanItem] }); }
                  }
                });
              }

              return sections.map((sec, sIdx) => {
                if (!sec.items) sec.items = [];
                if (!sec.title && sec.items.length === 0) return null;
                return (
                  <div key={`${ptIdx}-${sIdx}`} className="px-4 sm:px-6 py-4">
                    {sec.title && (
                      <div className="flex items-center gap-2 mb-4">
                        <span className="material-symbols-outlined text-sm text-primary">{getSectionIcon(sec.title)}</span>
                        <h4 className="text-sm font-bold text-text-dim uppercase tracking-widest">{sec.title}</h4>
                      </div>
                    )}
                    {sec.items.length > 0 && (
                      <ul className="space-y-3">
                        {sec.items.map((item, iIdx) => (
                          <li key={iIdx} className="flex items-start gap-2.5 group">
                            <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            <span className="text-sm text-text-muted leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              });
            })}
          </div>
        </div>
      )}

      {deliverTables.map((dTable, dIdx) => (
        <div key={`deliver-${dIdx}`} id={dTable.id} className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm scroll-mt-32 mt-8">
          <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light flex items-center justify-between">
            <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2">
              <TableIcon className="w-4 h-4 text-primary" />
              {dTable.heading || 'What we deliver'}
            </h3>
          </div>
          <div className="px-4 sm:px-6 py-4">
            {dTable.workflow && (
              <ul className="space-y-3">
                {dTable.workflow.map((item, iIdx) => {
                  const isPara = iIdx === 0 && !/^・/.test(item) && !/^•/.test(item) && !item.includes(':') && !item.includes('：');
                  const isJaDeliverIntro = item.includes('以下の成果物を提供します：');
                  const isEnDeliverIntro = item.includes('consisting of:');

                  if (isPara || isJaDeliverIntro || isEnDeliverIntro) {
                    return (
                      <div key={iIdx} className="mb-4 text-sm font-bold text-text-muted leading-relaxed">
                        {item}
                      </div>
                    )
                  }

                  let cleanItem = item.replace(/^[・.•]\s*/, '').replace(/^\d+\.\s/, '');
                  
                  // Check for #...# bold without checkmark
                  const hashBoldMatch = cleanItem.match(/^#(.+)#$/);
                  if (hashBoldMatch) {
                    return (
                      <li key={iIdx} className="flex items-start gap-2.5 group">
                        <span className="text-sm font-bold text-text-muted leading-relaxed">{hashBoldMatch[1]}</span>
                      </li>
                    )
                  }
                  
                  return (
                    <li key={iIdx} className="flex items-start gap-2.5 group">
                      <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      <span className="text-sm text-text-muted leading-relaxed">{cleanItem}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>
      ))}
    </>
  );
};


const WorkflowList = ({ workflow, serviceKey, tableHeading, isDeliverables = false }) => {
  if (!workflow || workflow.length === 0) return null;

  const hasNumberedTitles = workflow.some(item => /^\d+\.\s/.test(item));
  const hasBulletPoints = workflow.some(item => /^[・.•]/.test(item));

  const BulletIcon = () => isDeliverables 
    ? <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
    : <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />;

  const SubBulletIcon = () => isDeliverables
    ? <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 opacity-60 flex-shrink-0" />
    : <CheckCircle className="w-3 h-3 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />;

  if (!hasNumberedTitles && !hasBulletPoints) {
    // Check if this workflow has variant annotation items
    const hasVariantSection = workflow.includes('バリアント注釈および候補優先順位付け') || workflow.includes('Variant annotation and prioritization:');
    const variantSubtitles = ['バリアントの機能的・臨床的アノテーション', '遺伝形式および表現型情報に基づくフィルタリング', '予測影響、アレル頻度、疾患関連性に基づく候補バリアントの優先順位付け', 'Functional and clinical annotation of variants', 'Inheritance-based and phenotype-guided filtering', 'Candidate variant prioritization based on predicted impact, allele frequency, and disease relevance'];

    if (hasVariantSection) {
      return (
        <div className="space-y-4">
          {workflow.map((item, k) => {
            if (item === 'バリアント注釈および候補優先順位付け' || item === 'Variant annotation and prioritization:') {
              return (
                <div key={k} className="space-y-2">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    <span className="text-sm text-text-muted leading-relaxed">{item}</span>
                  </div>
                </div>
              );
            }
            if (variantSubtitles.includes(item)) {
              return (
                <div key={k} className="flex items-start gap-2.5 group ml-6">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                  <span className="text-sm text-text-muted leading-relaxed">{item}</span>
                </div>
              );
            }
            return (
              <div key={k} className="flex items-start gap-2.5 group">
                <BulletIcon />
                <span className="text-sm text-text-muted leading-relaxed">{(() => {
                  const hashBoldMatch = item.match(/^#(.+)#$/);
                  return hashBoldMatch ? hashBoldMatch[1] : item;
                })()}</span>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {workflow.map((item, k) => {
          // Check for #...# bold without checkmark
          const hashBoldMatch = item.match(/^#(.+)#$/);
          if (hashBoldMatch) {
            return (
              <div key={k} className="flex items-start gap-2.5 group">
                <span className="text-sm font-bold text-text-muted leading-relaxed">{hashBoldMatch[1]}</span>
              </div>
            );
          }
          // Check if item starts with bullet point "・" or "."
          if (/^[・.•]/.test(item)) {
            const cleanItem = item.replace(/^[・.•]\s*/, '');
            return (
              <div key={k} className="flex items-start gap-2.5 group ml-6">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                <span className="text-sm text-text-muted leading-relaxed">{cleanItem}</span>
              </div>
            );
          }
          // Check if item is marked with **...** for bold/larger text
          const boldMatch = item.match(/^\*\*(.+)\*\*$/);
          if (boldMatch) {
            return (
              <div key={k} className="flex items-start gap-2.5 group">
                <BulletIcon />
                <span className="text-lg sm:text-base font-bold text-text-muted leading-relaxed">{boldMatch[1]}</span>
              </div>
            );
          }
          // Check if item is marked with !...! for bold WITHOUT checkmark (smaller text for explanations)
          const noCheckBoldMatch = item.match(/^!(.+)!$/);
          if (noCheckBoldMatch) {
            return (
              <div key={k} className="flex items-start gap-2.5 group ml-6">
                <span className="text-sm text-text-muted leading-relaxed">{noCheckBoldMatch[1]}</span>
              </div>
            );
          }
          // Check if item is marked with #...# for bold WITHOUT checkmark (normal text size)
          const hashNoCheckMatch = item.match(/^#(.+)#$/);
          if (hashNoCheckMatch) {
            return (
              <div key={k} className="flex items-start gap-2.5 group">
                <span className="text-sm font-bold text-text-muted leading-relaxed">{hashNoCheckMatch[1]}</span>
              </div>
            );
          }
          return (
            <div key={k} className="flex items-start gap-2.5 group">
              <BulletIcon />
              <span className="text-sm text-text-muted leading-relaxed">{item}</span>
            </div>
          );
        })}
      </div>
    );
  }

  const items = [];
  let currentSection = null;

  workflow.forEach((item) => {
    const isNumberedTitle = /^\d+\.\s/.test(item);
    const isBulletPoint = /^[・.•]/.test(item);
    const isHoukoku = item.startsWith('報告書');
    const isToukeiKaiseki = item === '統計解析・可視化' || item === '統計解析・可視化:' || item === 'Statistical analysis and visualization'|| item === 'Statistical analysis and visualization:';
    const isSeigyoKaiseki = item === '制御解析' || item === 'Regulatory analysis' || item === 'Regulatory analysis:';
    const isDataQc = item === 'データQC・前処理' || item === 'Data QC and preprocessing' || item === 'Quality control and trimming' || item === 'Data QC and adapter trimming';
    const isIdenExpr = item === '遺伝子同定・発現定量' || item === 'Gene identification and expression quantification' || item === 'Gene identification and expression quantification:';
    const isFunAnnotation = item === '機能遺伝子アノテーション:' || item === 'Functional gene annotation:';
    const isGeneAnnotation = item === '遺伝子の分類群アノテーション（遺伝子を由来微生物／分類群に対応付け）' || item === 'Taxonomic annotation of genes (assignment of genes to microbial taxa/species)';
    const isVariantAnnotation = item === 'バリアント注釈および候補優先順位付け' || item === 'Variant annotation and prioritization:';
    const isVariantSubtitle = item === 'バリアントの機能的・臨床的アノテーション' || item === '遺伝形式および表現型情報に基づくフィルタリング' || item === '予測影響、アレル頻度、疾患関連性に基づく候補バリアントの優先順位付け' || item === 'Functional and clinical annotation of variants' || item === 'Inheritance-based and phenotype-guided filtering' || item === 'Candidate variant prioritization based on predicted impact, allele frequency, and disease relevance';
    const isTaxonomicProfiling = item === '転写活性を持つ微生物叢の分類群プロファイリング' || item === 'Taxonomic profiling of transcriptionally active microbiota';
    const isFuncAnnotation = item === '機能アノテーション・経路プロファイリング' || item === 'Functional annotation and pathway profiling' || item === 'Functional annotation and pathway profiling:';
    const isComparative = item === '群間比較・差次発現解析' || item === 'Comparative and multivariate analysis';
    const isMultivariate = item === '多変量解析・オーディネーション' || item === 'Multivariate analysis' || item === 'Multivariate analysis:';

    if (isNumberedTitle || isHoukoku) {
      currentSection = { title: item, subtitles: [], isHoukoku };
      items.push(currentSection);
    } else if (isToukeiKaiseki) {
      currentSection = { title: item, subtitles: [], isToukeiKaiseki: true };
      items.push(currentSection);
    } else if (isSeigyoKaiseki) {
      currentSection = { title: item, subtitles: [], isSeigyoKaiseki: true };
      items.push(currentSection);
    } else if (isFunAnnotation) {
      currentSection = { title: item, subtitles: [], isFunAnnotation: true };
      items.push(currentSection);
    } else if (isGeneAnnotation) {
      currentSection = { title: item, subtitles: [], isGeneAnnotation: true };
      items.push(currentSection);
    } else if (isVariantAnnotation) {
      currentSection = { title: item, subtitles: [], isVariantAnnotation: true };
      items.push(currentSection);
    } else if (isVariantSubtitle && currentSection && currentSection.isVariantAnnotation) {
      currentSection.subtitles.push(item);
    } else if (isDataQc) {
      currentSection = { title: item, subtitles: [], isDataQc: true };
      items.push(currentSection);
    } else if (isIdenExpr) {
      currentSection = { title: item, subtitles: [], isIdenExpr: true };
      items.push(currentSection);
    } else if (isTaxonomicProfiling) {
      currentSection = { title: item, subtitles: [], isTaxonomicProfiling: true };
      items.push(currentSection);
    } else if (isFuncAnnotation) {
      currentSection = { title: item, subtitles: [], isFuncAnnotation: true };
      items.push(currentSection);
    } else if (isComparative) {
      currentSection = { title: item, subtitles: [], isComparative: true };
      items.push(currentSection);
    } else if (isMultivariate) {
      currentSection = { title: item, subtitles: [], isMultivariate: true };
      items.push(currentSection);
    } else if (isBulletPoint && currentSection) {
      currentSection.subtitles.push(item.replace(/^[・.•]\s*/, ''));
    } else if (isBulletPoint && !currentSection) {
      currentSection = { title: '', subtitles: [item.replace(/^[・.•]\s*/, '')] };
      items.push(currentSection);
    } else if (currentSection && currentSection.subtitles.length > 0) {
      currentSection.subtitles.push(item);
    } else {
      currentSection = { title: item, subtitles: [] };
      items.push(currentSection);
    }
  });

  return (
    <div className="space-y-4">
      {items.map((section, sectionIndex) => {
        const isHeader = section.subtitles && section.subtitles.length > 0;
        const isToukeiKaiseki = section.isToukeiKaiseki;
        const isHoukoku = section.isHoukoku;
        const isSignalQuantNormalization = section.title === 'シグナル定量・正規化' || section.title === 'Signal quantification and normalization' || section.title === 'Signal quantification and normalization:';
        const isSeigyoKaiseki = section.isSeigyoKaiseki;
        const isFunAnnotation = section.isFunAnnotation;
        const isGeneAnnotation = section.isGeneAnnotation;
        const isDataQc = section.isDataQc;
        const isIdenExpr = section.isIdenExpr;
        const isVariantAnnotation = section.title === 'バリアント注釈および候補優先順位付け' || section.title === 'Variant annotation and prioritization:';
        const isSectionVariantAnnotation = section.isVariantAnnotation;
        const isTaxonomicProfiling = section.isTaxonomicProfiling;
        const isFuncAnnotation = section.isFuncAnnotation;
        const isComparative = section.isComparative;
        const isMultivariate = section.isMultivariate;

        if (isSignalQuantNormalization || isSeigyoKaiseki || isToukeiKaiseki || isFunAnnotation || isGeneAnnotation || isDataQc || isIdenExpr || isVariantAnnotation || isTaxonomicProfiling || isFuncAnnotation || isComparative || isMultivariate) {
          return (
            <div key={sectionIndex} className="space-y-2">
              {(isSignalQuantNormalization || isSeigyoKaiseki || isFunAnnotation || isToukeiKaiseki || isGeneAnnotation || isDataQc || isIdenExpr || isVariantAnnotation || isTaxonomicProfiling || isFuncAnnotation || isComparative || isMultivariate) ? (
                <>
                  <div className="flex items-start gap-2.5">
                    <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    <span className="text-sm text-text-muted leading-relaxed">{section.title.replace(/:$/, '')}</span>
                  </div>
                  {section.subtitles.length > 0 && (
                    <ul className="space-y-2 ml-6">
                      {(() => {
                        const elements = [];
                        let nestedItems = [];
                        
                        section.subtitles.forEach((subtitle, subtitleIndex) => {
                          const isNestedLevel = subtitle.match(/^\s{2}[•]\s/);
                          
                          if (isNestedLevel) {
                            // Collect nested items
                            nestedItems.push(subtitle.replace(/^\s{2}[•]\s/, ''));
                          } else {
                            // Flush previous nested items if any
                            if (nestedItems.length > 0) {
                              elements.push(
                                <ul key={`nested-${subtitleIndex}`} className="ml-4 mt-2 space-y-1.5">
                                  {nestedItems.map((nestedItem, nestedIndex) => (
                                    <li key={nestedIndex} className="flex items-start gap-2.5 group">
                                      <div className="w-1 h-1 rounded-full bg-brand-teal/60 mt-1.5 flex-shrink-0" />
                                      <span className="text-[11px] text-text-muted leading-relaxed">{nestedItem}</span>
                                    </li>
                                  ))}
                                </ul>
                              );
                              nestedItems = [];
                            }
                            
                            // Render regular subtitle
                            elements.push(
                              <li key={subtitleIndex} className="flex items-start gap-2.5 group">
                                <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                                <span className="text-sm text-text-muted leading-relaxed">{subtitle}</span>
                              </li>
                            );
                          }
                        });
                        
                        // Flush remaining nested items
                        if (nestedItems.length > 0) {
                          elements.push(
                            <ul key="nested-end" className="ml-4 mt-2 space-y-1.5">
                              {nestedItems.map((nestedItem, nestedIndex) => (
                                <li key={nestedIndex} className="flex items-start gap-2.5 group">
                                  <div className="w-1 h-1 rounded-full bg-brand-teal/60 mt-1.5 flex-shrink-0" />
                                  <span className="text-[11px] text-text-muted leading-relaxed">{nestedItem}</span>
                                </li>
                              ))}
                            </ul>
                          );
                        }
                        
                        return elements;
                      })()}
                    </ul>
                  )}
                </>
              ) : (
                <>
                  <h4 className="font-bold text-[15px] mb-4 text-text-main leading-snug">
                    {section.title.replace(/:$/, '')}
                  </h4>
                  {section.subtitles.length > 0 && (
                    <ul className="space-y-2 ml-6">
                      {section.subtitles.map((subtitle, subtitleIndex) => {
                      // Check for !...! bold without checkmark (smaller text for explanations)
                      const noCheckBoldMatch = subtitle.match(/^!(.+)!$/);
                      if (noCheckBoldMatch) {
                        return (
                          <li key={subtitleIndex} className="flex items-start gap-2.5 group ml-4">
                            <span className="text-sm text-text-muted leading-relaxed">{noCheckBoldMatch[1]}</span>
                          </li>
                        );
                      }
                      // Check for #...# bold without checkmark (normal text size)
                      const hashBoldMatch = subtitle.match(/^#(.+)#$/);
                      if (hashBoldMatch) {
                        return (
                          <li key={subtitleIndex} className="flex items-start gap-2.5 group ml-4">
                            <span className="text-sm font-bold text-text-muted leading-relaxed">{hashBoldMatch[1]}</span>
                          </li>
                        );
                      }
                      return (
                        <li key={subtitleIndex} className="flex items-start gap-2.5 group">
                          <SubBulletIcon />
                          <span className="text-sm text-text-muted leading-relaxed">{subtitle}</span>
                        </li>
                      );
                    })}
                    </ul>
                  )}
                </>
              )}
            </div>
          );
        }

        return (
          <div key={sectionIndex} className="space-y-2">
            {section.title && (
              <div className="flex items-start gap-2.5">
                {isDeliverables ? (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                    <span className="text-sm text-text-muted leading-relaxed">{section.title}</span>
                  </>
                ) : (
                  <>
                    {!isHeader && <BulletIcon />}
                    <span className={`text-sm ${isHeader ? 'font-bold text-text-main' : 'text-text-muted'} leading-relaxed`}>
                      {section.title}
                    </span>
                  </>
                )}
              </div>
            )}
            {section.subtitles.length > 0 && (
              <ul className="space-y-2 ml-6">
                {section.subtitles.map((subtitle, subtitleIndex) => {
                  // Check for !...! bold without checkmark (smaller text for explanations)
                  const noCheckBoldMatch = subtitle.match(/^!(.+)!$/);
                  if (noCheckBoldMatch) {
                    return (
                      <li key={subtitleIndex} className="flex items-start gap-2.5 group ml-4">
                        <span className="text-sm text-text-muted leading-relaxed">{noCheckBoldMatch[1]}</span>
                      </li>
                    );
                  }
                  // Check for #...# bold without checkmark (normal text size)
                  const hashBoldMatch = subtitle.match(/^#(.+)#$/);
                  if (hashBoldMatch) {
                    return (
                      <li key={subtitleIndex} className="flex items-start gap-2.5 group ml-4">
                        <span className="text-sm font-bold text-text-muted leading-relaxed">{hashBoldMatch[1]}</span>
                      </li>
                    );
                  }
                  return (
                    <li key={subtitleIndex} className="flex items-start gap-2.5 group">
                      {isDeliverables ? (
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                      ) : (
                        <SubBulletIcon />
                      )}
                      <span className="text-sm text-text-muted leading-relaxed">{subtitle}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TabularServiceRenderer = ({ tables, t, serviceKey }) => {
  return (
    <div className="space-y-0">
      {/* Mobile: stacked cards. Desktop: table layout */}
      {/* Desktop table (hidden on mobile) */}
      <div className="hidden md:block rounded-2xl border border-border-light shadow-sm bg-white overflow-hidden w-full">
        <table className="w-full text-left border-collapse mb-0">
          <thead>
            <tr className="bg-surface-50 border-b border-border-light">
              <th className="py-4 px-6 text-[13px] font-bold text-text-dim w-[40%] uppercase tracking-wide">
                {t('technicalService.analysisWorkflow')}
              </th>
              <th className="py-4 px-5 text-[13px] font-bold text-text-dim w-[40%] uppercase tracking-wide border-l border-border-light border-dashed">
                {t('technicalService.deliverables')}
              </th>
              <th className="py-4 px-5 text-[13px] font-bold text-text-dim w-[20%] uppercase tracking-wide border-l border-border-light border-dashed">
                {t('technicalService.priceAndTurnaround')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-light">
            {tables.map((table, i) => (
              <tr key={i} className="hover:bg-surface-50/50 transition-colors align-top">
                <td className="py-6 px-6">
                  {shouldShowWorkflowTableHeading(table.heading) && (
                    <h4 className="font-bold text-[15px] mb-4 text-text-main leading-snug break-words">{table.heading}</h4>
                  )}
                  <WorkflowList workflow={table.workflow} serviceKey={serviceKey} tableHeading={table.heading} />
                </td>
                <td className="py-6 px-5 border-l border-border-light border-dashed bg-surface-50/20">
                  <WorkflowList workflow={table.deliverables} serviceKey={serviceKey} tableHeading={table.heading} isDeliverables={true} />
                </td>
                <td className="py-6 px-5 border-l border-border-light border-dashed bg-surface-50/10">
                  <div className="flex flex-col gap-4 text-xs font-medium text-text-muted">
                    {table.price && (
                      <span className="flex items-start gap-2">
                        <Tag className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="leading-relaxed whitespace-pre-line">
                          {table.price.includes('Please contact us') ? (
                            <>Please <a href="/contact" className="hover:text-primary text-text-main underline underline-offset-4 font-bold">contact us</a> for a detailed quotation and estimated turnaround time.</>
                          ) : table.price}
                        </span>
                      </span>
                    )}
                    {table.turnaround && (
                      <span className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-text-dim flex-shrink-0 mt-0.5" />
                        <span className="leading-relaxed whitespace-pre-line">{table.turnaround}</span>
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked card layout (hidden on md+) */}
      <div className="md:hidden space-y-4">
        {tables.map((table, i) => (
          <div key={i} className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm">
            {/* Card header with heading */}
            {shouldShowWorkflowTableHeading(table.heading) && (
              <div className="bg-surface-50 px-4 py-3 border-b border-border-light">
                <h4 className="font-bold text-[15px] text-text-main leading-snug break-words">{table.heading}</h4>
              </div>
            )}
            {/* Workflow */}
            <div className="px-4 py-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="material-symbols-outlined text-sm text-primary">account_tree</span>
                <h5 className="text-[10px] font-bold text-text-dim uppercase tracking-widest">{t('technicalService.analysisWorkflow')}</h5>
              </div>
              <WorkflowList workflow={table.workflow} serviceKey={serviceKey} tableHeading={table.heading} />
            </div>
            {/* Deliverables */}
            {table.deliverables && table.deliverables.length > 0 && (
              <div className="px-4 py-4 border-t border-border-light bg-surface-50/30">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-3.5 h-3.5 text-brand-teal" />
                  <h5 className="text-[10px] font-bold text-text-dim uppercase tracking-widest">{t('technicalService.deliverables')}</h5>
                </div>
                <WorkflowList workflow={table.deliverables} serviceKey={serviceKey} tableHeading={table.heading} isDeliverables={true} />
              </div>
            )}
            {/* Price & Turnaround */}
            {(table.price || table.turnaround) && (
              <div className="px-4 py-4 border-t border-border-light bg-surface-50/10">
                <div className="flex items-center gap-2 mb-3">
                  <Tag className="w-3.5 h-3.5 text-primary" />
                  <h5 className="text-[10px] font-bold text-text-dim uppercase tracking-widest">{t('technicalService.priceAndTurnaround')}</h5>
                </div>
                <div className="flex flex-col gap-3 text-xs font-medium text-text-muted">
                  {table.price && (
                    <span className="leading-relaxed whitespace-pre-line">
                      {table.price.includes('Please contact us') ? (
                        <>Please <a href="/contact" className="hover:text-primary text-text-main underline underline-offset-4 font-bold">contact us</a> for a detailed quotation and estimated turnaround time.</>
                      ) : table.price}
                    </span>
                  )}
                  {table.turnaround && (
                    <span className="flex items-start gap-2">
                      <Clock className="w-4 h-4 text-text-dim flex-shrink-0 mt-0.5" />
                      <span className="leading-relaxed whitespace-pre-line">{table.turnaround}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const TechnicalServicePage = ({ data, serviceKey, translationKey, parentBreadcrumbs }) => {
  const { t } = useTranslation()

  const aiMlServiceKeys = [
    'patientStratification',
    'survivalRisk',
    'biomarkerDiscovery',
    'targetId',
    'hitDiscovery',
    'leadOptimization',
    'preclinicalBiomarker',
    'translationalPkPd',
    'cohortReanalysis'
  ]
  const isAiMlPage = serviceKey && aiMlServiceKeys.includes(serviceKey)

  let content = data
  if (translationKey) {
    const translated = t(translationKey, { returnObjects: true })
    if (translated && typeof translated === 'object' && !Array.isArray(translated)) {
      content = translated
    }
  } else if (serviceKey) {
    const translated = t(`servicesData.${serviceKey}`, { returnObjects: true })
    // Ensure we got an object back, not the key string
    if (translated && typeof translated === 'object' && !Array.isArray(translated)) {
      content = translated
    }
  }

  if (!content) return null;

  const breadcrumbs = [
    ...(parentBreadcrumbs || (serviceKey === 'scientificWriting'
      ? [
          { label: t('header.home'), path: '/' },
          { label: t('header.service'), path: '/service' }
        ]
      : [
          { label: t('header.home'), path: '/' },
          { label: t('header.service'), path: '/service' },
          { label: t('header.services.bioinformatics.title'), path: '/service/bioinformatics' }
        ])),
    { label: content.title }
  ]

  // Transform subtitle for clickable links
  let subtitleElement = content.subtitle
  if (serviceKey === 'transcriptomics' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|').map(part => part.trim())
    if (parts.length === 2) {
      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          <a
            href="#rna-seq"
            className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
            onClick={(e) => {
              e.preventDefault()
              document.getElementById('rna-seq')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            {parts[0]}
          </a>
          <span className="mx-2 opacity-50">|</span>
          <a
            href="#small-rna-seq"
            className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
            onClick={(e) => {
              e.preventDefault()
              document.getElementById('small-rna-seq')?.scrollIntoView({ behavior: 'smooth' })
            }}
          >
            {parts[1]}
          </a>
        </span>
      )
    }
  } else if (serviceKey === 'epigenomics' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|')
    if (parts.length === 4) {
      const scrollPairs = [
        { text: parts[0].trim(), id: 'atac-chip' },
        { text: parts[1].trim(), id: 'atac-chip' },
        { text: parts[2].trim(), id: 'cut-run-tag' },
        { text: parts[3].trim(), id: 'cut-run-tag' }
      ]

      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          {scrollPairs.map((pair, idx) => (
            <React.Fragment key={idx}>
              <a
                href={`#${pair.id}`}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {pair.text}
              </a>
              {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">|</span>}
            </React.Fragment>
          ))}
        </span>
      )
    }
  } else if (serviceKey === 'massSpec' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|')
    if (parts.length === 2) {
      const scrollPairs = [
        { text: parts[0].trim(), id: 'proteomics' },
        { text: parts[1].trim(), id: 'metabolomics' }
      ]

      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          {scrollPairs.map((pair, idx) => (
            <React.Fragment key={idx}>
              <a
                href={`#${pair.id}`}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {pair.text}
              </a>
              {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">|</span>}
            </React.Fragment>
          ))}
        </span>
      )
    }
  } else if (serviceKey === 'microbiome' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|')
    if (parts.length === 3) {
      const scrollPairs = [
        { text: parts[0].trim(), id: 'shotgun' },
        { text: parts[1].trim(), id: 'amplicon' },
        { text: parts[2].trim(), id: 'metatranscriptomics' }
      ]

      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          {scrollPairs.map((pair, idx) => (
            <React.Fragment key={idx}>
              <a
                href={`#${pair.id}`}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {pair.text}
              </a>
              {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">|</span>}
            </React.Fragment>
          ))}
        </span>
      )
    }
  } else if (serviceKey === 'genomics' && content.subtitle) {
    // EN uses " | "; JP uses "/" between short-read and long-read labels
    const sep = content.subtitle.includes('|') ? '|' : content.subtitle.includes('/') ? '/' : null
    if (sep) {
      const parts = content.subtitle.split(sep).map((p) => p.trim()).filter(Boolean)
      if (parts.length === 2) {
        const scrollPairs = [
          { text: parts[0], id: 'wgs-wes' },
          { text: parts[1], id: 'long-read-wgs' }
        ]

        subtitleElement = (
          <span className="flex flex-wrap gap-y-1">
            {scrollPairs.map((pair, idx) => (
              <React.Fragment key={idx}>
                <a
                  href={`#${pair.id}`}
                  className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault()
                    document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  {pair.text}
                </a>
                {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">{sep}</span>}
              </React.Fragment>
            ))}
          </span>
        )
      }
    }
  } else if (serviceKey === 'singleCell' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|')
    if (parts.length === 4) {
      const scrollPairs = [
        { text: parts[0].trim(), id: 'scrna-seq' },
        { text: parts[1].trim(), id: 'scatac-seq' },
        { text: parts[2].trim(), id: 'single-cell-proteomics' },
        { text: parts[3].trim(), id: 'single-cell-metabolomics' }
      ]

      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          {scrollPairs.map((pair, idx) => (
            <React.Fragment key={idx}>
              <a
                href={`#${pair.id}`}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {pair.text}
              </a>
              {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">|</span>}
            </React.Fragment>
          ))}
        </span>
      )
    }
  } else if (serviceKey === 'spatialOmics' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|')
    if (parts.length === 3) {
      const scrollPairs = [
        { text: parts[0].trim(), id: 'spatial-transcriptomics' },
        { text: parts[1].trim(), id: 'spatial-proteomics' },
        { text: parts[2].trim(), id: 'spatial-metabolomics' }
      ]

      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          {scrollPairs.map((pair, idx) => (
            <React.Fragment key={idx}>
              <a
                href={`#${pair.id}`}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {pair.text}
              </a>
              {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">|</span>}
            </React.Fragment>
          ))}
        </span>
      )
    }
  } else if (serviceKey === 'pipelineDev' && content.subtitle && content.subtitle.includes('|')) {
    const parts = content.subtitle.split('|')
    if (parts.length === 2) {
      const scrollPairs = [
        { text: parts[0].trim(), id: 'pipeline-development' },
        { text: parts[1].trim(), id: 'pipeline-development' }
      ]

      subtitleElement = (
        <span className="flex flex-wrap gap-y-1">
          {scrollPairs.map((pair, idx) => (
            <React.Fragment key={idx}>
              <a
                href={`#${pair.id}`}
                className="text-primary hover:text-primary/80 underline underline-offset-4 transition-colors cursor-pointer"
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(pair.id)?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                {pair.text}
              </a>
              {idx < scrollPairs.length - 1 && <span className="mx-2 opacity-50">|</span>}
            </React.Fragment>
          ))}
        </span>
      )
    }
  }

  if (serviceKey === 'dataSecurity') {
    // Data security special rendering
    return (
      <ServiceLayout
        breadcrumbs={breadcrumbs}
        title={content.title}
        subtitle={subtitleElement}
      >
        <div className="space-y-16">
          <p className="text-lg text-text-muted max-w-4xl">{content.intro}</p>

          {content.modules && content.modules.map((module, i) => (
            <div key={i} id={module.id} className="animate-in fade-in slide-in-from-bottom-4 duration-700 scroll-mt-32" style={{ animationDelay: `${i * 100}ms` }}>
              {serviceKey !== 'scientificWriting' && serviceKey !== 'agricultureFood' && (
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5" />
                  </div>
                  <h2 className="text-2xl font-display font-bold text-text-main">{module.title}</h2>
                </div>
              )}

              {module.subtitle && (
                <p className="text-base text-text-muted mb-4 max-w-3xl leading-relaxed">
                  {module.subtitle}
                </p>
              )}

              {module.intro && (
                <p className="text-base text-text-muted mb-8 max-w-3xl leading-relaxed">
                  {module.intro}
                </p>
              )}

              <div className="space-y-4">
                {module.tables && module.tables.map((table, j) => {
                  const iconMap = {
                    'box-standard': Cloud,
                    'amazon-s3-enhanced': Shield,
                    'encrypted-hdd-offline': HardDrive
                  };
                  const IconComponent = iconMap[table.id] || Shield;
                  return (
                    <div
                      key={j}
                      id={table.id}
                      className="bio-card-tinted rounded-xl p-4 sm:p-6"
                    >
                      {(serviceKey !== 'scientificWriting' || (table.id !== 'what-we-provide' && table.id !== 'what-we-deliver')) && (
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-display font-bold text-lg text-text-main">{table.heading}</h3>
                            <div className="h-1 w-12 bg-primary/20 rounded-full mt-1"></div>
                          </div>
                        </div>
                      )}
                      {table.workflow && table.workflow.map((item, k) => (
                        <div key={k} className="mb-4">
                          <h4 className="text-sm font-bold text-text-dim uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-xs">link</span>
                            {t('technicalService.dataSecuritySections.usage')}
                          </h4>
                          <p className="text-sm text-text-muted leading-relaxed pl-5">{item}</p>
                        </div>
                      ))}
                      {table.deliverables && table.deliverables.map((item, k) => {
                        let sectionTitle = '';
                        let icon = 'security';
                        if (k === 0) {
                          sectionTitle = t('technicalService.dataSecuritySections.controls');
                          icon = 'shield';
                        } else if (k === 1) {
                          sectionTitle = t('technicalService.dataSecuritySections.recommendedCase');
                          icon = 'check_circle';
                        } else if (k === 2) {
                          sectionTitle = t('technicalService.dataSecuritySections.storagePeriod');
                          icon = 'schedule';
                        }
                        return (
                          <div key={k} className="mb-4">
                            <h4 className="text-sm font-bold text-text-dim uppercase tracking-widest mb-2 flex items-center gap-1.5">
                              <span className="material-symbols-outlined text-xs">{icon}</span>
                              {sectionTitle}
                            </h4>
                            <p className="text-sm text-text-muted leading-relaxed pl-5">{item}</p>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <ServiceCTA />
        </div>
      </ServiceLayout>
    );
  }

  return (
    <ServiceLayout
      breadcrumbs={breadcrumbs}
      title={content.title}
      subtitle={subtitleElement}
    >
      <div className="flex flex-col lg:flex-row gap-12">
        <div className="flex-1 min-w-0 space-y-12">
        <div className="space-y-2 text-lg text-text-muted max-w-4xl">
          <p className="leading-relaxed whitespace-pre-line">{content.intro}</p>
        </div>

        {content.modules && content.modules.map((module, i) => (
          <div key={i} id={module.id} className="animate-in fade-in slide-in-from-bottom-4 duration-700 scroll-mt-32" style={{ animationDelay: `${i * 100}ms` }}>
            {translationKey !== 'bioinformatics.categories.nutritionCosmetics' && serviceKey !== 'scientificWriting' && serviceKey !== 'agricultureFood' && serviceKey !== 'multiOmics' && serviceKey !== 'biomarkerDiscovery' && serviceKey !== 'cohortReanalysis' && serviceKey !== 'targetId' && serviceKey !== 'hitDiscovery' && serviceKey !== 'leadOptimization' && serviceKey !== 'preclinicalBiomarker' && serviceKey !== 'translationalPkPd' && serviceKey !== 'patientStratification' && serviceKey !== 'survivalRisk' && (
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <span className="material-symbols-outlined">{module.icon || 'analytics'}</span>
                </div>
                <h2 className="text-2xl font-display font-bold text-text-main">{module.title}</h2>
              </div>
            )}

            {module.subtitle && (
              <p className="text-base text-text-muted mb-4 max-w-3xl leading-relaxed">
                {module.subtitle}
              </p>
            )}

            {module.intro && (
              <p className="text-base text-text-muted mb-8 max-w-3xl leading-relaxed">
                {module.intro}
              </p>
            )}

            {module.infographic && (
              <div className="rounded-2xl overflow-hidden border border-border-light shadow-sm bg-surface-50/50 p-2 sm:p-4 animate-in fade-in duration-1000" style={{ marginBottom: '30px' }}>
                <img
                  src={module.infographic}
                  alt={module.title}
                  className="w-full h-auto rounded-xl shadow-inner object-contain"
                  loading="lazy"
                />
              </div>
            )}

            {module.inputs && serviceKey !== 'spatialOmics' && serviceKey !== 'agricultureFood' && serviceKey !== 'hitDiscovery' && serviceKey !== 'leadOptimization' && (
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1.5 mb-8">
                <span className="text-[13px] font-bold text-text-main flex items-center gap-1.5 flex-shrink-0">
                  <Info className="w-4 h-4 text-primary" /> {t('technicalService.supportedInputs')}
                </span>
                <span className="text-[13px] sm:text-[14px] text-text-muted leading-relaxed break-words">
                  {module.inputs.reduce((acc, input, idx, arr) => {
                    if (idx === 0) return input;
                    if (idx === arr.length - 1) {
                      return `${acc}, or ${input}`;
                    }
                    return `${acc}, ${input}`;
                  }, '')}
                </span>
              </div>
            )}

            <div className="space-y-8">
              {serviceKey === 'agricultureFood' ? (
                <ThreeColumnServiceRenderer tables={module.tables} t={t} serviceKey={serviceKey} />
              ) : isAiMlPage ? (
                serviceKey === 'biomarkerDiscovery' ? (
                  <ThreeColumnServiceRenderer tables={module.tables} t={t} serviceKey={serviceKey} />
                ) : (
                  <AiMlTableRenderer tables={module.tables} t={t} />
                )
              ) : (() => {
                  // Check if this module is SCP or SCM (simple content format without table structure)
                  const isSimpleContentModule = module.id === 'single-cell-proteomics' || module.id === 'single-cell-metabolomics';

                  if (isSimpleContentModule && module.tables && module.tables.length > 0) {
                    const table = module.tables[0];
                    return (
                      <div key={module.id} className="space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-1000">
                        {/* Analysis Items Grid */}
                        {table.workflow && table.workflow.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {table.workflow.map((item, k) => (
                              <div 
                                key={k} 
                                className="group p-5 rounded-2xl border border-border-light bg-surface-50/50 hover:bg-white hover:shadow-lg hover:shadow-primary/5 hover:border-primary/20 transition-all duration-300"
                              >
                                <div className="flex items-start gap-4">
                                  {(() => {
                                    const hashBoldMatch = item.match(/^#(.+)#$/);
                                    if (hashBoldMatch) return null;
                                    return (
                                      <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                                        {k === 0 ? <Activity className="w-5 h-5" /> :
                                         k === 1 ? <Layers className="w-5 h-5" /> :
                                         k === 2 ? <Microscope className="w-5 h-5" /> :
                                         <Sparkles className="w-5 h-5" />}
                                      </div>
                                    );
                                  })()}
                                  <div className="space-y-1">
                                    <h4 className="text-[15px] font-bold text-text-main group-hover:text-primary transition-colors">
                                      {(() => {
                                        const hashBoldMatch = item.match(/^#(.+)#$/);
                                        return hashBoldMatch ? hashBoldMatch[1] : item;
                                      })()}
                                    </h4>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Integrated Analysis Research Note */}
                        {table.deliverables && table.deliverables.length > 0 && (
                          <div className="pl-6 border-l border-primary/20 mt-8 mb-4 max-w-2xl">
                            <p className="text-base md:text-lg font-bold italic text-text-muted leading-relaxed whitespace-pre-line">
                              {table.deliverables.join(' ')}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  const shouldRenderAsTable = !isAiMlPage &&
                    serviceKey !== 'dataSecurity' &&
                    serviceKey !== 'agricultureFood' &&
                    serviceKey !== 'scientificWriting' &&
                    module.tables &&
                    module.tables.some(t => t.deliverables && t.deliverables.length > 0 && (t.price || t.turnaround));

                  if (shouldRenderAsTable) {
                    return <TabularServiceRenderer tables={module.tables} t={t} serviceKey={serviceKey} />;
                  }

                  // Fallback or other table types
                  return module.tables && module.tables.map((table, j) => (
                  <div key={j} id={table.id} className="bio-card-static rounded-2xl overflow-hidden border border-border-light shadow-sm scroll-mt-32">
                    <div className="bg-surface-50 px-4 sm:px-6 py-4 border-b border-border-light flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
                      {shouldShowWorkflowTableHeading(table.heading) && (
                        <h3 className="font-display font-bold text-sm text-text-main flex items-center gap-2">
                          <TableIcon className="w-4 h-4 text-primary" />
                          {table.heading}
                        </h3>
                      )}
                      {(table.price || table.turnaround) && (
                        <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest">
                          {table.price && (
                            <span className="flex items-center gap-1 text-primary whitespace-pre-line">
                              <Tag className="w-3 h-3" /> {table.price.includes('Please contact us') ? (
                                <>Please <a href="/contact" className="hover:text-primary/80 underline underline-offset-4">contact us</a> for a detailed quotation and estimated turnaround time.</>
                              ) : table.price}
                            </span>
                          )}
                          {table.turnaround && (
                            <span className="flex items-center gap-1 text-text-dim whitespace-pre-line">
                              <Clock className="w-3 h-3" /> {table.turnaround}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Conditional grid: if there are deliverables, show 2 columns. Otherwise, full width. */}
                    <div className={(!isAiMlPage && serviceKey !== 'agricultureFood' && table.deliverables && table.deliverables.length > 0) ? 'grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border-light w-full' : 'w-full'}>

                      {/* Workflow */}
                      <div className="p-4 sm:p-6">
                        {serviceKey !== 'spatialOmics' && serviceKey !== 'nutritionCosmetics' && translationKey !== 'bioinformatics.categories.nutritionCosmetics' && serviceKey !== 'scientificWriting' && serviceKey !== 'multiOmics' && serviceKey !== 'pipelineDev' && (
                          <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-sm text-primary">account_tree</span>
                            <h4 className="text-sm font-bold text-text-dim uppercase tracking-widest">
                              {t('technicalService.analysisWorkflow')}
                            </h4>
                          </div>
                        )}
                        
                        {/* Special rendering for pipeline-dev page */}
                        {serviceKey === 'pipelineDev' ? (
                          <div className="space-y-6">
                            {(() => {
                              const sections = [];
                              let currentSection = null;
                              
                              (table.workflow || []).forEach((item) => {
                                if (/^\d+\.\s/.test(item)) {
                                  currentSection = { title: item, items: [] };
                                  sections.push(currentSection);
                                } else if (currentSection) {
                                  currentSection.items.push(item);
                                }
                              });

                              return sections.map((section, idx) => (
                                <div key={idx} className="space-y-2">
                                  <div className="flex items-start gap-2.5">
                                    <CheckCircle className="w-3.5 h-3.5 text-primary mt-1 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                    <span className="text-sm font-bold text-text-muted leading-relaxed">{section.title}</span>
                                  </div>
                                  {section.items.length > 0 && (
                                    <div className="ml-6 space-y-1">
                                      {section.items.map((subItem, subIdx) => (
                                        <div key={subIdx} className="text-sm text-text-muted">• {subItem}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>
                        ) : (
                          <WorkflowList workflow={table.workflow} serviceKey={serviceKey} tableHeading={table.heading} />
                        )}
                      </div>

                      {/* Deliverables - only show for non-AI/ML pages and not for agriculture-food page */}
                      {!isAiMlPage && serviceKey !== 'agricultureFood' && table.deliverables && table.deliverables.length > 0 && (
                        <div className="p-4 sm:p-6 bg-surface-50/30">
                          <div className="flex items-center gap-2 mb-4">
                            <FileText className="w-3.5 h-3.5 text-brand-teal" />
                            <h4 className="text-sm font-bold text-text-dim uppercase tracking-widest">{t('technicalService.deliverables')}</h4>
                          </div>
                          <ul className="space-y-3">
                            {table.deliverables.map((item, k) => {
                              // Handle bullet points in deliverables
                              if (/^[・.•]/.test(item)) {
                                const cleanItem = item.replace(/^[・.•]\s*/, '');
                                return (
                                  <li key={k} className="flex items-start gap-2.5 ml-6">
                                    <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                                    <span className="text-sm text-text-muted leading-relaxed whitespace-pre-line">{cleanItem}</span>
                                  </li>
                                );
                              }
                              return (
                                <li key={k} className="flex items-start gap-2.5">
                                  <div className="w-1.5 h-1.5 rounded-full bg-brand-teal mt-2 flex-shrink-0" />
                                  <span className="text-sm text-text-muted leading-relaxed whitespace-pre-line">{item}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        ))}

        {/* Global Pricing/Contact Note */}
        <ServiceCTA />
      </div>

      {/* Sidebar - sticky on desktop */}
      <aside className="lg:w-56 flex-shrink-0 lg:border-l lg:border-border-light lg:pl-6">
        <div className="lg:sticky lg:top-28 space-y-2">
          <a href="/service-process" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all text-text-dim hover:text-text-main hover:bg-surface-100">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings w-4 h-4"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path><circle cx="12" cy="12" r="3"></circle></svg>
            <span>{t('header.serviceProcess')}</span>
          </a>
          <a href="/service/data-security" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all text-text-dim hover:text-text-main hover:bg-surface-100">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield w-4 h-4"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"></path></svg>
            <span>{t('header.dataSecurity')}</span>
          </a>
          <a href="/contact" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all text-text-dim hover:text-text-main hover:bg-surface-100">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-mail w-4 h-4"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
            <span>{t('header.contactUs')}</span>
          </a>
        </div>
      </aside>
    </div>
    </ServiceLayout>
  )
}

export default TechnicalServicePage
